import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { IconArchive, IconArrowBackUp, IconFileImport, IconPencil } from "@tabler/icons-react";
import {
  AdminPageHeader,
  Badge,
  BotonExportarExcel,
  Button,
  Card,
  Group,
  IconAction,
  Input,
  PaginadorTabla,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useConfirmDialog,
} from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { qk } from "@/lib/query-keys";
import { formatearMonto } from "@/shared/format/money";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { ContratoAnualizadoAdmin, ContratoAnualizadoResumen } from "@/lib/types";
import { useContratosAdmin, useDarDeBajaContrato, useReactivarContrato } from "../hooks/use-contratos";
import {
  importarContratoXml,
  listarContratos,
  previewContratoXml,
} from "../services/contratos-service";
import { ContratoPreviewModal } from "./ContratoPreviewModal";

const PAGE_SIZE = 15;

export function AdminContratosPage() {
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const [filtro, setFiltro] = useState("");
  const [page, setPage] = useState(1);
  const [filtroDebounced] = useDebouncedValue(filtro, 350);

  // Búsqueda + paginado SERVER-SIDE (query params con debounce).
  const serverParams = useMemo(
    () => ({
      ...(filtroDebounced.trim() ? { busqueda: filtroDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [filtroDebounced, page],
  );

  // Cambiar la búsqueda invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtroDebounced]);

  const { data } = useContratosAdmin(serverParams);
  const contratos = data?.items ?? [];
  const total = data?.total ?? 0;

  const darDeBaja = useDarDeBajaContrato();
  const reactivar = useReactivarContrato();
  const qc = useQueryClient();

  // Carga desde XML: elegir archivo → previsualizar → confirmar.
  const fileRef = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<ContratoAnualizadoAdmin | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const onElegirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-elegir el mismo archivo
    if (!file) return;
    try {
      const dto = await previewContratoXml(file);
      setArchivo(file);
      setPreview(dto);
    } catch (err) {
      toastErrorApi(err);
    }
  };

  const confirmarImportar = async () => {
    if (!archivo) return;
    setConfirmando(true);
    try {
      const dto = await importarContratoXml(archivo);
      toastExito({ title: `Contrato ${dto.clave} cargado`, message: "Importado desde XML." });
      void qc.invalidateQueries({ queryKey: qk.adminContratosAnualizados });
      setPreview(null);
      setArchivo(null);
    } catch (err) {
      toastErrorApi(err);
    } finally {
      setConfirmando(false);
    }
  };

  // Export a Excel · trae TODAS las páginas con la búsqueda actual.
  const obtenerTodos = async (): Promise<ContratoAnualizadoResumen[]> => {
    const busqueda = filtroDebounced.trim();
    const todos: ContratoAnualizadoResumen[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarContratos({
        ...(busqueda ? { busqueda } : {}),
        page: p,
        pageSize: 100,
      });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  // Por (clave, año fiscal): si hay una versión activa y cuál es la última · define las
  // acciones por fila (una clave puede repetirse en años distintos). Se calcula sobre la
  // página visible: el backend ordena por clave + versión desc, así que las versiones de
  // una clave llegan contiguas (solo un grupo podría partirse en el borde de página).
  const infoPorGrupo = useMemo(() => {
    const m = new Map<string, { hayActiva: boolean; maxVersion: number }>();
    for (const c of contratos) {
      const k = `${c.clave}|${c.anioFiscal ?? ""}`;
      const cur = m.get(k) ?? { hayActiva: false, maxVersion: 0 };
      m.set(k, { hayActiva: cur.hayActiva || c.activo, maxVersion: Math.max(cur.maxVersion, c.version) });
    }
    return m;
  }, [contratos]);

  const abrirEditar = (id: string) => void navigate({ to: "/admin/contratos-anualizados/$id", params: { id } });

  const confirmarDarDeBaja = async (c: { id: string; clave: string; anioFiscal: number | null }) => {
    const ok = await confirm({
      title: `Dar de baja el contrato ${c.clave}`,
      message: (
        <Text size="sm">
          El contrato <strong>{c.clave}</strong>{c.anioFiscal ? ` (${c.anioFiscal})` : ""} dejará
          de estar disponible para solicitudes nuevas. <strong>No se elimina</strong>: se conserva
          el histórico y la trazabilidad de las solicitudes que ya lo referencian, y podrás
          reactivarlo después.
        </Text>
      ),
      confirmLabel: "Dar de baja",
      confirmColor: "advertencia",
      icon: <IconArchive size={14} />,
    });
    if (ok) darDeBaja.mutate(c.id);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AdminPageHeader
        title="Contratos anualizados"
        description={`Mock administrable de SAP · ${total} contrato(s).`}
        actions={
          <Button
            variant="light"
            leftSection={<IconFileImport size={14} />}
            onClick={() => fileRef.current?.click()}
          >
            Importar CA desde XML
          </Button>
        }
      />

      <input
        ref={fileRef}
        type="file"
        accept=".xml,text/xml,application/xml"
        onChange={onElegirArchivo}
        className="absolute h-px w-px opacity-0"
        style={{ pointerEvents: "none" }}
      />
      <ContratoPreviewModal
        contrato={preview}
        confirmando={confirmando}
        onConfirm={confirmarImportar}
        onCancel={() => {
          setPreview(null);
          setArchivo(null);
        }}
      />

      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Input
            placeholder="Buscar por clave, proveedor o RFC…"
            value={filtro}
            onChange={(e) => setFiltro(e.currentTarget.value)}
            wrapperClassName="max-w-90 flex-1"
          />
          <BotonExportarExcel
            archivo="contratos-anualizados"
            hoja="Contratos anualizados"
            obtenerFilas={obtenerTodos}
            columnas={[
              { header: "Clave", value: (c) => c.clave },
              { header: "Año fiscal", value: (c) => c.anioFiscal },
              { header: "Versión", value: (c) => c.version },
              { header: "Proveedor", value: (c) => c.proveedorNombre },
              { header: "RFC", value: (c) => c.proveedorRfc },
              { header: "Fecha", value: (c) => c.fecha },
              { header: "Total", value: (c) => c.total },
              { header: "Moneda", value: (c) => c.moneda },
              { header: "Conceptos", value: (c) => c.conceptos },
              { header: "Estado", value: (c) => (c.activo ? "Activo" : "Baja") },
            ]}
          />
        </Group>

        <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <Card padding="lg">
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm">
              <Thead>
                <Tr>
                  <Th>Clave</Th>
                  <Th align="center">Año fiscal</Th>
                  <Th align="center">Versión</Th>
                  <Th>Proveedor</Th>
                  <Th>RFC</Th>
                  <Th align="right">Total</Th>
                  <Th align="center">Conceptos</Th>
                  <Th align="center">Estado</Th>
                  <Th />
                </Tr>
              </Thead>
              {!data ? (
                <TablaSkeleton dentroDeTabla columnas={[{}, { align: "center" }, { align: "center" }, {}, {}, { align: "right" }, { align: "center" }, { align: "center" }, { align: "right" }]} filas={PAGE_SIZE} />
              ) : (
                <Tbody>
                  {contratos.map((c) => {
                    const info = infoPorGrupo.get(`${c.clave}|${c.anioFiscal ?? ""}`);
                    const esUltimaInactiva = !c.activo && !info?.hayActiva && c.version === info?.maxVersion;
                    return (
                      <Tr key={c.id} className={c.activo ? undefined : "opacity-55"}>
                        <Td className="font-mono">{c.clave}</Td>
                        <Td align="center"><Text size="xs" className="font-mono">{c.anioFiscal ?? "—"}</Text></Td>
                        <Td align="center"><Text size="xs" className="font-mono">v{c.version}</Text></Td>
                        <Td>{c.proveedorNombre}</Td>
                        <Td><Text size="xs" className="font-mono">{c.proveedorRfc}</Text></Td>
                        <Td align="right">{formatearMonto(c.total, c.moneda)}</Td>
                        <Td align="center"><Badge variant="light" color="slate">{c.conceptos}</Badge></Td>
                        <Td align="center">
                          <Badge variant="light" color={c.activo ? "exito" : "slate"}>
                            {c.activo ? "Activo" : "Histórico"}
                          </Badge>
                        </Td>
                        <Td align="right">
                          <Group gap={4} className="flex-nowrap justify-end">
                            {c.activo && (
                              <>
                                <IconAction label="Editar (crea versión nueva)" size="sm" onClick={() => abrirEditar(c.id)}>
                                  <IconPencil size={14} />
                                </IconAction>
                                <IconAction label="Dar de baja" size="sm" iconColor="advertencia-600" onClick={() => confirmarDarDeBaja(c)}>
                                  <IconArchive size={14} />
                                </IconAction>
                              </>
                            )}
                            {esUltimaInactiva && (
                              <IconAction label="Reactivar" size="sm" iconColor="exito-600" loading={reactivar.isPending} onClick={() => reactivar.mutate(c.id)}>
                                <IconArrowBackUp size={14} />
                              </IconAction>
                            )}
                          </Group>
                        </Td>
                      </Tr>
                    );
                  })}
                  {contratos.length === 0 && (
                    <Tr>
                      <Td colSpan={9}>
                        <Group justify="center" className="py-4">
                          <Text c="dimmed" size="sm">Sin contratos anualizados.</Text>
                        </Group>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              )}
            </Table>
          </div>
        </Card>

        <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="contratos" />
      </Stack>
    </div>
  );
}
